import { orderSourceLabel } from '@/utils/orderSource';
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import {
  AlertTriangle,
  Clock,
  ExternalLink,
  FileText,
  MapPin,
  Truck,
  User,
} from 'lucide-react';
import { toast } from 'sonner';

import { getDeliveryMediaBlob } from '@/api/deliveries';
import { createPodReceiptPath } from '@/api/pod';
import { isDesktopApp } from '@/api/desktopSync';
import { Card } from '@/components/ui/card';
import DesktopReceiptModal from '@/components/delivery/DesktopReceiptModal';
import { useAuth } from '@/context/AuthContext';
import { formatDeliveryTime, orderNumberSuffix } from '@/utils/deliveryLog';


const colorMap = {
  green: { bg: 'bg-green-50', border: 'border-green-200', text: 'text-green-700', icon: 'text-green-600' },
  blue: { bg: 'bg-blue-50', border: 'border-blue-200', text: 'text-blue-700', icon: 'text-blue-600' },
  red: { bg: 'bg-red-50', border: 'border-red-200', text: 'text-red-700', icon: 'text-red-600' },
  stone: { bg: 'bg-stone-50', border: 'border-stone-200', text: 'text-stone-600', icon: 'text-stone-400' },
};

export function DeliveryLogSection({ icon: Icon, label, count, color, children }) {
  const palette = colorMap[color];
  return (
    <div data-testid={`log-section-${label.toLowerCase().replace(' ', '-')}`}>
      <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${palette.bg} ${palette.border} border mb-2`}>
        <Icon className={`h-4 w-4 ${palette.icon}`} />
        <span className={`text-sm font-semibold ${palette.text}`}>{label}</span>
        <span className={`text-xs ${palette.text} opacity-70`}>({count})</span>
      </div>
      <div className="space-y-1.5 pl-1">{children}</div>
    </div>
  );
}

const RecipientLine = ({
  order,
  nameClassName = 'text-sm font-medium text-stone-900',
  addressClassName = 'text-xs text-stone-500',
}) => (
  <>
    <p className={`${nameClassName} mt-1 truncate`} data-testid={`log-recipient-${order.id}`}>
      {order.recipient_name || 'Unknown'}
    </p>
    <p className={`${addressClassName} truncate`}>
      {order.recipient_street_address}
      {order.recipient_street_address_2 && `, ${order.recipient_street_address_2}`}
      {order.recipient_city && `, ${order.recipient_city}`}
    </p>
  </>
);

const OrderNumberBlock = ({ order, decoration, muted = false }) => {
  const numberClass = muted ? 'text-stone-400' : 'text-stone-900';
  const labelClass = muted ? 'text-stone-400' : 'text-stone-500';
  return (
    <div className="flex flex-col items-center w-14 flex-shrink-0 pt-0.5">
      <span className={`text-lg font-bold font-mono ${numberClass} leading-tight`} data-testid={`log-order-num-${order.id}`}>
        {orderNumberSuffix(order.internal_order_number)}
      </span>
      {orderSourceLabel(order, '') && (
        <span className={`text-[10px] font-semibold ${labelClass} mt-0.5`} data-testid={`log-source-${order.id}`}>
          {orderSourceLabel(order)}
        </span>
      )}
      {decoration}
    </div>
  );
};

export function AuthenticatedDeliveryThumbnail({ media, alt, className, owner }) {
  const { getToken } = useAuth();
  const [src, setSrc] = useState(null);

  useEffect(() => {
    if (!media?.storage_path) return undefined;
    const session = owner?.capture();
    if (owner && !session) return undefined;
    const token = session?.token || getToken();
    if (!token) return undefined;
    let objectUrl;
    let cancelled = false;
    const load = async () => {
      try {
        const loadedUrl = URL.createObjectURL(await getDeliveryMediaBlob(
          media.storage_path,
          { token },
        ));
        if (cancelled || (session && !session.isCurrent())) URL.revokeObjectURL(loadedUrl);
        else {
          objectUrl = loadedUrl;
          setSrc({ url: objectUrl, path: media.storage_path, owner });
        }
      } catch { /* thumbnail is optional */ }
    };
    load();
    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [media?.storage_path, getToken, owner]);

  if (!src || src.path !== media?.storage_path || src.owner !== owner
    || (owner && !owner.isCurrent())) return null;
  return <img src={src.url} alt={alt} className={className} />;
}

export function DeliveredCard({ order, owner }) {
  const navigate = useNavigate();
  const isDesktop = isDesktopApp();
  const [openingReceipt, setOpeningReceipt] = useState(false);
  const [showDesktopReceipt, setShowDesktopReceipt] = useState(false);
  const receiptAttempt = useRef(null);
  const currentOwner = useRef(owner);
  currentOwner.current = owner;
  useLayoutEffect(() => {
    setOpeningReceipt(false);
    setShowDesktopReceipt(false);
    return () => {
      const attempt = receiptAttempt.current;
      if (attempt) {
        attempt.controller.abort();
        if (!attempt.published && attempt.target && !attempt.target.closed) attempt.target.close();
        receiptAttempt.current = null;
      }
    };
  }, [owner, order.id]);
  const confirmation = order.delivery_confirmation || {};
  const mediaFiles = confirmation.media_files || [];
  const signatureIndex = mediaFiles.findIndex((media) => media.file_type === 'signature');
  const photoIndices = mediaFiles.reduce(
    (indices, media, index) => (media.file_type === 'photo' ? [...indices, index] : indices),
    [],
  );
  const hasMedia = signatureIndex >= 0 || photoIndices.length > 0;
  const gps = confirmation.gps_location;
  const hasGps = gps?.latitude && gps?.longitude && !(gps.latitude === 0 && gps.longitude === 0);
  const deliveredTime = formatDeliveryTime(confirmation.time_delivered || confirmation.confirmed_at);

  const openReceipt = async () => {
    if (receiptAttempt.current) return;
    const session = owner?.capture();
    if (!session || !session.isCurrent()) return;
    // Desktop app (online or offline): render the receipt in-app. The window.open
    // popup boots a second SPA with no backend URL — it fails to fetch and, before
    // the AuthContext fix, wiped the shared session. The web keeps the popup.
    if (isDesktop) { setShowDesktopReceipt(true); return; }
    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const target = isMobile ? null : window.open('', '_blank');
    if (target) target.opener = null;
    const attempt = { target, controller: new AbortController(), published: false };
    receiptAttempt.current = attempt;
    const isCurrent = () => receiptAttempt.current === attempt
      && currentOwner.current === owner && session.isCurrent();
    setOpeningReceipt(true);
    try {
      const path = await createPodReceiptPath(order.id, {
        token: session.token, signal: attempt.controller.signal,
      });
      if (!isCurrent() || target?.closed) return;
      attempt.published = true;
      if (isMobile || !target) navigate(path);
      else target.location.href = path;
    } catch (error) {
      if (target && !target.closed && !attempt.published) target.close();
      if (isCurrent()) toast.error(error.message || 'Unable to open receipt');
    } finally {
      if (isCurrent()) setOpeningReceipt(false);
      if (receiptAttempt.current === attempt) receiptAttempt.current = null;
    }
  };

  const gpsDecoration = (
    <div className={`mt-1 w-5 h-5 rounded-full flex items-center justify-center ${
      hasGps ? 'bg-green-100 text-green-600' : 'bg-stone-100 text-stone-400'
    }`} title={hasGps ? 'GPS captured' : 'No GPS'}>
      <MapPin className="h-3 w-3" />
    </div>
  );

  return (
    <>
    <Card className="p-3 hover:shadow-md transition-shadow" data-testid={`log-card-${order.id}`}>
      <div className="flex gap-3">
        <OrderNumberBlock order={order} decoration={gpsDecoration} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-primary px-1.5 py-0.5 rounded bg-primary/10" data-testid={`log-driver-${order.id}`}>
              {confirmation.confirmed_by_user_name || 'Driver'}
            </span>
            {deliveredTime && (
              <span className="text-xs text-stone-400 flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {deliveredTime}
              </span>
            )}
          </div>
          <RecipientLine order={order} />
          <div className="flex items-center gap-1 mt-1.5 text-xs text-stone-600">
            <User className="h-3 w-3 text-stone-400" />
            <span data-testid={`log-received-${order.id}`}>
              {confirmation.received_by}
              {confirmation.received_by_relationship && <span className="text-stone-400"> ({confirmation.received_by_relationship})</span>}
            </span>
          </div>
          {confirmation.notes && <p className="text-xs text-stone-400 mt-1 truncate italic">&ldquo;{confirmation.notes}&rdquo;</p>}
        </div>
        <div className="flex flex-col items-end gap-2 flex-shrink-0">
          {hasMedia && (
            <div className="flex gap-1">
              {signatureIndex >= 0 && (
                <AuthenticatedDeliveryThumbnail
                  owner={owner}
                  media={mediaFiles[signatureIndex]}
                  alt="Sig"
                  className="h-10 w-14 object-contain rounded border border-stone-200 bg-white"
                />
              )}
              {photoIndices.slice(0, 2).map((index, photoNumber) => (
                <AuthenticatedDeliveryThumbnail
                  owner={owner}
                  key={index}
                  media={mediaFiles[index]}
                  alt={`Photo ${photoNumber + 1}`}
                  className="h-10 w-10 object-cover rounded border border-stone-200"
                />
              ))}
              {photoIndices.length > 2 && (
                <div className="h-10 w-10 rounded border border-stone-200 bg-stone-100 flex items-center justify-center text-xs text-stone-500 font-medium">
                  +{photoIndices.length - 2}
                </div>
              )}
            </div>
          )}
          <button
            type="button"
            onClick={openReceipt}
            disabled={openingReceipt}
            className="flex items-center gap-1 text-xs text-green-700 font-medium hover:underline"
            data-testid={`log-receipt-${order.id}`}
          >
            <FileText className="h-3 w-3" />
            {openingReceipt ? 'Opening...' : 'Receipt'}
            <ExternalLink className="h-2.5 w-2.5" />
          </button>
        </div>
      </div>
    </Card>
      {showDesktopReceipt && (
        <DesktopReceiptModal order={order} onClose={() => setShowDesktopReceipt(false)} />
      )}
    </>
  );
}

export function OnRouteCard({ order }) {
  return (
    <Card className="p-3 border-blue-100 hover:shadow-md transition-shadow" data-testid={`log-card-${order.id}`}>
      <div className="flex gap-3">
        <OrderNumberBlock order={order} decoration={<Truck className="h-4 w-4 text-blue-500 mt-1" />} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            {order.assigned_driver_name && (
              <span className="text-xs font-medium text-blue-700 px-1.5 py-0.5 rounded bg-blue-50 border border-blue-200" data-testid={`log-driver-${order.id}`}>
                {order.assigned_driver_name}
              </span>
            )}
            {order.run_name && <span className="text-xs text-stone-400">{order.run_name}</span>}
          </div>
          <RecipientLine order={order} />
        </div>
      </div>
    </Card>
  );
}

const failureLabels = { nobody_home: 'Nobody home', wrong_address: 'Wrong address', other: 'Other' };

export function FailedCard({ order }) {
  const failure = order.delivery_failure || {};
  return (
    <Card className="p-3 border-red-100 hover:shadow-md transition-shadow" data-testid={`log-card-${order.id}`}>
      <div className="flex gap-3">
        <OrderNumberBlock order={order} decoration={<AlertTriangle className="h-4 w-4 text-red-500 mt-1" />} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs font-medium text-red-700 px-1.5 py-0.5 rounded bg-red-50 border border-red-200">
              {failureLabels[failure.reason] || failure.reason || 'Failed'}
            </span>
            {failure.failed_by_user_name && <span className="text-xs text-stone-400">{failure.failed_by_user_name}</span>}
          </div>
          <RecipientLine order={order} />
          {failure.details && <p className="text-xs text-red-600 mt-1 truncate italic">&ldquo;{failure.details}&rdquo;</p>}
        </div>
      </div>
    </Card>
  );
}

export function UndeliveredCard({ order }) {
  const statusLabel = {
    new: 'New',
    accepted: 'Accepted',
    designed: 'Designed',
  }[order.status] || order.status;
  return (
    <Card className="p-3 opacity-75 hover:opacity-100 hover:shadow-md transition-all" data-testid={`log-card-${order.id}`}>
      <div className="flex gap-3">
        <OrderNumberBlock
          order={order}
          muted
          decoration={<span className="text-[10px] text-stone-400 mt-1">{statusLabel}</span>}
        />
        <div className="flex-1 min-w-0">
          <RecipientLine
            order={order}
            nameClassName="text-sm font-medium text-stone-700"
            addressClassName="text-xs text-stone-400"
          />
        </div>
      </div>
    </Card>
  );
}
